import { Component, OnInit } from '@angular/core';
import { SubscriptionService } from '../../../store/services/subscription.service';
import { ISubscriptionV2 } from '../../../store/models/subscription.model';
import { IAccount } from '../../../store/models/account.model';
import { DiscountsService } from '../../../store/services/discounts.service';
import { IPlanProduct } from '../../../store/models/discount.model';
import { ISelectItem } from '../../../store/models/select.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { IDialogComponent } from '../../../shared/ui/dialog/dialog.component';

@Component({
  selector: 'app-manage-subscription-dialog',
  templateUrl: './manage-subscription-dialog.component.html',
  styleUrls: ['./manage-subscription-dialog.component.scss'],
})
export class ManageSubscriptionDialogComponent implements OnInit, IDialogComponent<ManageSubscriptionDialogComponent> {

  subscription: ISubscriptionV2;
  account: IAccount;
  hasSubscription = true;
  inputForm: FormGroup;

  plans: IPlanProduct[] = [];

  componentReady = false;

  constructor(
    private readonly subscriptionService: SubscriptionService,
    private readonly discountService: DiscountsService,
  ) {
  }

  get currentPlan(): IPlanProduct {
    return this.plans.find(p => p.id === this.subscription.product_id);
  }

  get planItems(): ISelectItem[] {
    return this.plans.map(p => ({
      label: p.name,
      value: p.plan_id,
    }));
  }

  get isLegacyStripe(): boolean {
    return !!this.subscription?.legacy_stripe;
  }

  get showSelfBillWarning(): boolean {
    return !!this.subscription?.paid_by_manager && !this.inputForm?.get('manager_pay')?.value;
  }

  get legacyStripeWarning(): string {
    const manualStripeNote =
      'Plan changes and subscription updates must be handled manually in Stripe.';
    if (this.subscription?.paid_by_manager) {
      return `This user's subscription is paid by their manager and is on a legacy Stripe subscription. ${manualStripeNote}`;
    }
    return `This user is on a legacy Stripe subscription. ${manualStripeNote}`;
  }

  initForm() {
    if (!this.inputForm) {
      return;
    }

    this.inputForm.addControl('plan_id', new FormControl(this.currentPlan ? this.currentPlan.plan_id : '', [Validators.required]));
    this.inputForm.addControl('customer_id', new FormControl(this.subscription.customer_id, [Validators.required]));
    this.inputForm.addControl('discount', new FormControl(this.subscription.team_code, []));
    this.inputForm.addControl('discard_discount', new FormControl(false, []));
    this.inputForm.addControl('manager_pay', new FormControl(this.subscription.paid_by_manager, []));

    if (this.isLegacyStripe) {
      this.inputForm.get('plan_id')?.disable();
      this.inputForm.get('discount')?.disable();
      this.inputForm.get('discard_discount')?.disable();
    }

    this.componentReady = true;
  }

  ngOnInit(): void {
    this.subscriptionService.view(this.account.account_id)
      .subscribe(subscription => {
        this.subscription = subscription;
        this.discountService.getPlanProducts()
          .subscribe(plans => {
            this.plans = plans.items;
            // init logic here
            this.initForm();
          });
      }, error => {
        this.inputForm.addControl('customer_id', new FormControl('', [Validators.required]));
        this.componentReady = true;
        this.hasSubscription = false;
      });
  }

  getSubscriptionAmount(): string {
    if (!this.subscription.subscription_discount) {
      return this.subscription.amount;
    }
    if (this.subscription.discount_percentage) {
      // tslint:disable-next-line:max-line-length
      return (parseInt(this.subscription.amount, 10) - ((parseInt(this.subscription.subscription_discount, 10) * parseInt(this.subscription.amount, 10)) / 100)).toFixed(2);
    }

    return (parseInt(this.subscription.amount, 10) - parseInt(this.subscription.subscription_discount, 10)).toFixed(2);
  }

  getTotalCost(): string {
    const amount = parseFloat(String(this.subscription.amount ?? '0').replace(/,/g, '')) || 0;
    const subordinates =
      parseFloat(String(this.subscription.cost_subordinates ?? '0').replace(/,/g, '')) || 0;
    return (amount + subordinates).toFixed(2);
  }

  getSubscriptionSetup(): string {
    // tslint:disable-next-line:max-line-length
    return (parseInt(this.subscription.amount, 10) - ((parseInt(this.subscription.subscription_discount, 10) * parseInt(this.subscription.amount, 10)) / 100)).toFixed(2);
  }

  toggleDiscount($event: MatSlideToggleChange) {
    this.inputForm.get('discard_discount').setValue($event.checked);
  }

}
